import { useCallback, useMemo, useState } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { getPlatformAPI, type ReminderPermission } from '@/src/renderer/lib/platform'
import type { ReminderDraft } from '@/src/renderer/lib/db/models'

export interface ReminderActions {
  add: (draft: ReminderDraft) => void
  remove: (reminderId: string) => void
  /** Nur im Browser gesetzt — am Desktop regelt das System die Erlaubnis selbst. */
  permission: ReminderPermission | undefined
  requestPermission: () => void
}

/**
 * **Erinnerungen anlegen und entfernen** — geteilt vom Chip in den Aufgaben-Details und vom
 * Aufgaben-Menü. Im Browser fragt das erste Anlegen nach der Erlaubnis (Plan, Etappe 4), und zwar
 * NOCH IM KLICK: Safari und Firefox ignorieren die Frage sonst.
 */
export function useReminderActions(taskId: string | undefined): ReminderActions {
  const source = useDataSource()
  const platform = getPlatformAPI()
  const delivery = platform.reminders
  const web = platform.platform === 'web'
  const [permission, setPermission] = useState<ReminderPermission | undefined>(() => (web ? delivery?.getPermission() : undefined))

  const requestPermission = useCallback(() => {
    if (!delivery || !web) return
    void delivery.requestPermission().then(setPermission)
  }, [delivery, web])

  const add = useCallback((draft: ReminderDraft) => {
    if (!taskId) return
    if (permission === 'default') requestPermission()
    void source.createReminder(taskId, draft)
  }, [permission, requestPermission, source, taskId])

  const remove = useCallback((reminderId: string) => { void source.deleteReminder(reminderId) }, [source])

  return useMemo(() => ({ add, remove, permission, requestPermission }), [add, remove, permission, requestPermission])
}
